import React, { useState, useEffect } from 'react';
import {
  X,
  Search,
  Layers,
  FileText,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';
import { DrilldownRecordItem, AnalysisDataSource, AnalysisQueryFilter } from '../types';
import { analysisService } from '../analysisService';
import { useEscapeKey } from '../../../shared/useGlobalModalEscape';

interface AnalysisDrilldownModalProps {
  isOpen: boolean;
  onClose: () => void;
  widgetTitle: string;
  dataSource: AnalysisDataSource;
  dimension: string;
  dimensionValue?: string;
  filters: AnalysisQueryFilter;
  onNavigateToRecord?: (dataSource: AnalysisDataSource, recordId: string) => void;
}

export const AnalysisDrilldownModal: React.FC<AnalysisDrilldownModalProps> = ({
  isOpen,
  onClose,
  widgetTitle,
  dataSource,
  dimension,
  dimensionValue,
  filters,
  onNavigateToRecord,
}) => {
  useEscapeKey(onClose, isOpen);

  const [records, setRecords] = useState<DrilldownRecordItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedRecord, setSelectedRecord] = useState<DrilldownRecordItem | null>(null);

  const pageSize = 8;

  useEffect(() => {
    if (!isOpen) {
      setSelectedRecord(null);
      setSearchQuery('');
      setCurrentPage(1);
      return;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    analysisService
      .getDrilldown({
        dataSource,
        dimension,
        dimensionValue,
        filters,
      })
      .then((data) => {
        if (isMounted) {
          setRecords(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err.message || 'Failed to load underlying records');
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, dataSource, dimension, dimensionValue, filters]);

  if (!isOpen) return null;

  // Filtered by local search query
  const filteredRecords = records.filter((r) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.recordNumber.toLowerCase().includes(q) ||
      r.title.toLowerCase().includes(q) ||
      (r.category && r.category.toLowerCase().includes(q)) ||
      (r.department && r.department.toLowerCase().includes(q)) ||
      (r.severity && r.severity.toLowerCase().includes(q))
    );
  });

  const totalPages = Math.ceil(filteredRecords.length / pageSize) || 1;
  const paginatedRecords = filteredRecords.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getSourceLabel = (src: AnalysisDataSource) => {
    switch (src) {
      case 'reports':
        return 'Frontline Safety Ingestion Reports';
      case 'occurrences':
        return 'Safety Occurrences & Incidents';
      case 'investigations':
        return 'Safety Investigations';
      case 'audits':
        return 'Quality Audit Findings';
      case 'actions':
        return 'CAPA & Corrective Actions';
      case 'assets':
        return 'Aviation Fleet Assets';
    }
  };

  const getSeverityBadge = (sev?: string) => {
    const s = (sev || 'NORMAL').toUpperCase();
    if (s === 'CRITICAL') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '9999px',
            fontSize: '11px',
            fontWeight: 700,
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
            border: '1px solid #fca5a5',
          }}
        >
          <AlertCircle size={11} /> CRITICAL
        </span>
      );
    }
    if (s === 'HIGH' || s === 'MAJOR') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '9999px',
            fontSize: '11px',
            fontWeight: 700,
            backgroundColor: '#fef3c7',
            color: '#b45309',
            border: '1px solid #fcd34d',
          }}
        >
          <AlertTriangle size={11} /> HIGH
        </span>
      );
    }
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
          borderRadius: '9999px',
          fontSize: '11px',
          fontWeight: 600,
          backgroundColor: '#eff6ff',
          color: '#1d4ed8',
          border: '1px solid #bfdbfe',
        }}
      >
        <CheckCircle2 size={11} /> {s}
      </span>
    );
  };

  return (
    <div
      className="modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-card modal-xl"
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
          width: '100%',
          maxWidth: '1080px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #cbd5e1',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            backgroundColor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FileText size={20} />
            </div>
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a', margin: 0 }}>
                {widgetTitle} — Drill-Down Records
              </h2>
              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', display: 'flex', gap: '8px' }}>
                <span>{getSourceLabel(dataSource)}</span>
                {dimensionValue && (
                  <>
                    <span>•</span>
                    <span style={{ color: '#2563eb', fontWeight: 600 }}>
                      Filter: {dimension}: {dimensionValue}
                    </span>
                  </>
                )}
                <span>•</span>
                <span>{records.length} Total Records Found</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="modal-close-btn"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search & Filter Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 24px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #f1f5f9',
            gap: '16px',
          }}
        >
          <div
            style={{
              position: 'relative',
              flex: 1,
              maxWidth: '400px',
            }}
          >
            <Search
              size={15}
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
              }}
            />
            <input
              type="text"
              placeholder="Search by record number, title, department..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                fontSize: '12px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Page {currentPage} of {totalPages} (Displaying: {paginatedRecords.length} of {filteredRecords.length})
          </div>
        </div>

        {/* Modal Body / Table View */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px' }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  border: '3px solid #2563eb',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  margin: '0 auto 12px auto',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <span style={{ fontSize: '13px' }}>Loading underlying records...</span>
            </div>
          ) : error ? (
            <div style={{ padding: '36px', textAlign: 'center', color: '#dc2626' }}>
              <AlertCircle size={28} style={{ margin: '0 auto 8px auto' }} />
              <div style={{ fontSize: '13px', fontWeight: 600 }}>{error}</div>
            </div>
          ) : paginatedRecords.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
              <Layers size={36} style={{ margin: '0 auto 10px auto', opacity: 0.5 }} />
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                No records found matching filter criteria
              </div>
              <p style={{ fontSize: '12px', marginTop: '4px' }}>
                Try clearing your search term or broadening global filters.
              </p>
            </div>
          ) : (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '12px',
                textAlign: 'left',
                marginTop: '12px',
              }}
            >
              <thead>
                <tr
                  style={{
                    borderBottom: '2px solid #e2e8f0',
                    color: '#475569',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    backgroundColor: '#f8fafc',
                  }}
                >
                  <th style={{ padding: '10px 12px' }}>Record No</th>
                  <th style={{ padding: '10px 12px' }}>Date</th>
                  <th style={{ padding: '10px 12px' }}>Title & Description</th>
                  <th style={{ padding: '10px 12px' }}>Category / Type</th>
                  <th style={{ padding: '10px 12px' }}>Department / Location</th>
                  <th style={{ padding: '10px 12px' }}>Severity</th>
                  <th style={{ padding: '10px 12px' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.map((item) => (
                  <tr
                    key={item.id}
                    style={{
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background-color 0.15s ease',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedRecord(item)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0f9ff')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <td style={{ padding: '12px', fontWeight: 700, color: '#2563eb' }}>
                      {item.recordNumber}
                    </td>
                    <td style={{ padding: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                      {item.date || '-'}
                    </td>
                    <td style={{ padding: '12px', maxWidth: '280px' }}>
                      <div
                        style={{
                          fontWeight: 600,
                          color: '#1e293b',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {item.title}
                      </div>
                      {item.description && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#94a3b8',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginTop: '2px',
                          }}
                        >
                          {item.description}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px', color: '#475569' }}>
                      {item.category || '-'}
                    </td>
                    <td style={{ padding: '12px', color: '#64748b' }}>
                      {item.department || item.location || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{getSeverityBadge(item.severity)}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '2px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: '#f1f5f9',
                          color: '#475569',
                        }}
                      >
                        {item.status || 'NORMAL'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRecord(item);
                        }}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          backgroundColor: '#ffffff',
                          color: '#2563eb',
                          fontSize: '11px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Selected Record Detail Drawer / Preview */}
        {selectedRecord && (
          <div
            style={{
              padding: '16px 24px',
              backgroundColor: '#f8fafc',
              borderTop: '2px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                  {selectedRecord.recordNumber} — {selectedRecord.title}
                </span>
                {getSeverityBadge(selectedRecord.severity)}
              </div>
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Close Preview ✕
              </button>
            </div>
            {selectedRecord.description ? (
              <p style={{ fontSize: '12px', color: '#334155', lineHeight: 1.5, margin: 0 }}>
                {selectedRecord.description}
              </p>
            ) : (
              <p style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>
                No additional narrative text provided for this record.
              </p>
            )}
            <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
              <span>Date: {selectedRecord.date || 'N/A'}</span>
              <span>Category: {selectedRecord.category || 'N/A'}</span>
              <span>Department / Station: {selectedRecord.department || selectedRecord.location || 'N/A'}</span>
              <span>Status: {selectedRecord.status || 'N/A'}</span>
            </div>
          </div>
        )}

        {/* Modal Footer / Pagination */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 24px',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
          }}
        >
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                backgroundColor: currentPage <= 1 ? '#f1f5f9' : '#ffffff',
                color: currentPage <= 1 ? '#94a3b8' : '#334155',
                cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
              }}
            >
              <ChevronLeft size={14} /> Previous
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                backgroundColor: currentPage >= totalPages ? '#f1f5f9' : '#ffffff',
                color: currentPage >= totalPages ? '#94a3b8' : '#334155',
                cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
              }}
            >
              Next <ChevronRight size={14} />
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            style={{ width: 'auto', padding: '6px 16px', fontSize: '12px' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
